import { createCustomFilterForEntity } from "@shared/backend/create-custom-filter-for-entity";
import {
  filterWithRelationsAndJson,
  type RelationalOrJsonEntityFilter,
} from "@shared/helpers/sql-relations";
import { dbNamesOf, SqlDatabase } from "remult";
import { OpportunityParticipant } from "./";

const createCustomFilter =
  createCustomFilterForEntity<OpportunityParticipant>();

export class OpportunityParticipantFilters {
  /**
   * # 🌟✨ HyperFilter – The Ultimate Filtering Powerhouse!
   * ### 🤝 Filter through complex relations effortlessly
   * ### 📦 Directly filter JSON fields (no backend work required!)
   * ### 🚀 Transforms your filter into a single, optimized DB query
   * ### 🔒 Engineered for maximum SQL injection protection
   *
   * ---
   * ### Example:
   * ```ts
   * myEntityRepo.find({
   *  where: MyEntity.hyperFilter({ ... })
   * })
   * ```
   *
   * ---
   * Built by Natan Salmon
   */
  static hyperFilter = createCustomFilter<
    RelationalOrJsonEntityFilter<OpportunityParticipant>
  >((filters) => {
    return filterWithRelationsAndJson(OpportunityParticipant)(filters);
  });

  static get dbNames() {
    return dbNamesOf(OpportunityParticipant, {
      tableName: true,
    });
  }

  /** Approved-style individual registration: no group on the participant row. */
  static isIndividualParticipant = createCustomFilter(() => {
    return SqlDatabase.rawFilter(async (build) => {
      const dbNames = await this.dbNames;
      build.sql = `(${dbNames.groupId} IS NULL OR ${dbNames.groupId} = '')`;
    });
  });

  static isLocationInRadiusFromPoint = createCustomFilter<{
    lat: number;
    lng: number;
    radius: {
      distance: number;
      measure: "km" | "m";
    };
  }>((coordinates) => {
    return SqlDatabase.rawFilter(async (build) => {
      const {
        lat,
        lng,
        radius: { distance, measure },
      } = coordinates;

      const dbNames = await this.dbNames;
      const radiusMeters = measure === "km" ? distance * 1000 : distance;

      const epTable = dbNames;
      const oppTable = dbNames.opportunity;

      build.sql = `
      EXISTS (
        SELECT 1
        FROM opportunity o
        JOIN ${epTable} ep ON ep.opportunity = o.id
        WHERE ep.id = ${epTable}.id AND (
          (${build.param(lat)}::float = 0 AND ${build.param(lng)}::float = 0)
          OR ST_DWithin(
            o.coordinates_geog,
            ST_SetSRID(ST_MakePoint(${build.param(lng)}, ${build.param(lat)}), 4326)::geography,
            ${build.param(radiusMeters)}
          )
        )
      )
    `;
    });
  });
}
